import { useEffect, useRef } from "react";
import { motion, useDragControls, useReducedMotion } from "framer-motion";
import { Minus, X } from "lucide-react";
import { useWindows, type WindowState } from "./context";
import { getDef } from "./registry";
import { useIsMobile } from "@/hooks";

export function WindowFrame({ win }: { win: WindowState }) {
    const { close, focus, minimize, focusedId, constraintsRef } = useWindows();
    const isMobile = useIsMobile();
    const reduce = useReducedMotion();
    const controls = useDragControls();
    const ref = useRef<HTMLElement>(null);
    const focused = focusedId === win.id;
    const def = getDef(win.id);

    useEffect(() => {
        if (!win.minimized) ref.current?.focus({ preventScroll: true });
    }, [win.minimized]);

    if (!def) return null;
    const Icon = def.icon;

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
            e.stopPropagation();
            close(win.id);
        }
    };

    const header = (
        <header
            className="os-window-header"
            data-testid={`window-header-${win.id}`}
            onPointerDown={(e) => controls.start(e)}
        >
            <Icon size={15} strokeWidth={2.2} style={{ color: def.color, flex: "none" }} />
            <span className="flex-1 truncate font-mono text-[11.5px] text-inksoft">{def.title}</span>
            <button
                className={isMobile ? "tap-btn" : "dot-btn bg-amber-400"}
                data-testid={`window-minimize-${win.id}`}
                aria-label={`Minimize ${def.title}`}
                onClick={(e) => {
                    e.stopPropagation();
                    minimize(win.id);
                }}
            >
                <Minus />
            </button>
            <button
                className={isMobile ? "tap-btn" : "dot-btn bg-rose-400"}
                data-testid={`window-close-${win.id}`}
                aria-label={`Close ${def.title}`}
                onClick={(e) => {
                    e.stopPropagation();
                    close(win.id);
                }}
            >
                <X />
            </button>
        </header>
    );

    if (isMobile) {
        return (
            <>
                <motion.button
                    type="button"
                    aria-label={`Close ${def.title}`}
                    data-testid={`window-scrim-${win.id}`}
                    className="fixed inset-0 bg-ink/35 backdrop-blur-[1px]"
                    style={{ zIndex: 4990 + win.z, display: win.minimized ? "none" : "block" }}
                    initial={reduce ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => close(win.id)}
                />
                <motion.section
                    ref={ref as React.RefObject<HTMLElement>}
                    tabIndex={-1}
                    role="dialog"
                    aria-label={def.title}
                    data-testid={`window-${win.id}`}
                    onKeyDown={onKeyDown}
                    onPointerDown={() => focus(win.id)}
                    drag={reduce ? false : "y"}
                    dragListener={false}
                    dragControls={controls}
                    dragMomentum={false}
                    dragElastic={{ top: 0, bottom: 0.5 }}
                    dragConstraints={{ top: 0, bottom: 0 }}
                    onDragEnd={(_, info) => {
                        if (info.offset.y > 120 || info.velocity.y > 700) close(win.id);
                    }}
                    initial={reduce ? false : { y: "100%" }}
                    animate={{ y: 0 }}
                    exit={reduce ? undefined : { y: "100%" }}
                    transition={{ type: "spring", stiffness: 320, damping: 32 }}
                    className="os-sheet"
                    style={{ zIndex: 5000 + win.z, display: win.minimized ? "none" : "flex" }}
                >
                    <div
                        className="flex-none cursor-grab pb-0.5 pt-2.5 touch-none"
                        data-testid={`window-grabber-${win.id}`}
                        onPointerDown={(e) => controls.start(e)}
                    >
                        <span className="mx-auto block h-1.5 w-11 rounded-full bg-sand" />
                    </div>
                    {header}
                    <div className="os-window-body os-scroll">{def.render()}</div>
                    <button
                        type="button"
                        className="os-sheet-close"
                        data-testid={`sheet-close-${win.id}`}
                        onClick={() => close(win.id)}
                    >
                        close {def.title}
                    </button>
                </motion.section>
            </>
        );
    }

    return (
        <motion.section
            ref={ref as React.RefObject<HTMLElement>}
            tabIndex={-1}
            role="dialog"
            aria-label={def.title}
            data-testid={`window-${win.id}`}
            onKeyDown={onKeyDown}
            onPointerDown={() => focus(win.id)}
            drag={!reduce}
            dragListener={false}
            dragControls={controls}
            dragMomentum={false}
            dragElastic={0}
            dragConstraints={constraintsRef}
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16 }}
            style={{
                x: win.x,
                y: win.y,
                zIndex: 100 + win.z,
                width: `min(${def.width ?? 640}px, calc(100vw - 40px))`,
                display: win.minimized ? "none" : "flex",
            }}
            className={`os-window ${focused ? "focused" : ""}`}
        >
            {header}
            <div className="os-window-body os-scroll">{def.render()}</div>
        </motion.section>
    );
}
